import { FilterWidget } from '../search-core/FilterWidget.js';
import { PaginationWidget } from '../search-core/PaginationWidget.js';
import { SearchWidget } from '../search-core/SearchWidget.js';
import { SortWidget } from '../search-core/SortWidget.js';
import { utils } from '../tools/Utils.js';
import { ViewWidget } from '../view/ViewWidget.js';
import { AirDatepickerWidget } from '../widgets/AirDatepickerWidget.js';
import { ArchivalDateWidget } from '../widgets/ArchivalDateWidget.js';
import { AsyncWidget } from '../widgets/async/AsyncWidget.js';
import { AutocompleteWidget } from '../widgets/autocomplete/AutocompleteWidget.js';
import { CaptchaWidget } from '../widgets/CaptchaWidget.js';
import { CollectionWidget } from '../widgets/collection/CollectionWidget.js';
import { ComboBoxWidget } from '../widgets/ComboBoxWidget.js';
import { DropdownWidget } from '../widgets/DropdownWidget.js';
import { FilesizeWidget } from '../widgets/FilesizeWidget.js';
import { InputCollectionWidget } from '../widgets/InputCollectionWidget.js';
import { ListboxWidget } from '../widgets/listbox/ListboxWidget.js';
import { MenuWidget } from '../widgets/MenuWidget.js';
import { NumboxWidget } from '../widgets/NumboxWidget.js';
import { RadioboxWidget } from '../widgets/RadioboxWidget.js';
import { TableWidget } from '../widgets/table/TableWidget.js';
import { TabsPanelWidget } from '../widgets/TabsPanelWidget.js';
import { TabsWidget } from '../widgets/TabsWidget.js';
import { TimecodeWidget } from '../widgets/TimecodeWidget.js';
import { TripletWidget } from '../widgets/TripletWidget.js';
import { DefaultServerCmdProvider } from './cmd/DefaultServerCmdProvider.js';
import { ServerCmdProviderRegistry } from './cmd/ServerCmdProviderRegistry.js';
import { DefaultUiCommandProvider } from './imcmd/DefaultUiCommandProvider.js';
import { UiCommandRegistry } from './imcmd/UiCommandRegistry.js';
import { ImSys } from './ImSys.js';
import { ItemsUiCommandProvider } from './items/ItemsUiCommandProvider.js';
import { WidgetRegistry } from './WidgetRegistry.js';

// WidgetsInit.ts
// конкретные виджеты
/**
 * Определяем пути к библиотекам динамически подгружаемых виджетов
 * @returns Путь до файла в зависимости от сборки
 */
/** Путь до vendor-сборки стилей Uppy (см. work/vendor/uppy/uppy.css). */
const UPPY_VENDOR_CSS = '/src/vs/min/uppy.css';
/** Путь до vendor-сборки стилей gridstack (см. work/vendor/gridstack.css). */
const GRIDSTACK_VENDOR_CSS = '/src/vs/min/gridstack.css';
/** Путь до vendor-сборки темы ymaps (см. work/vendor/ymaps.css). */
const YMAPS_VENDOR_CSS = '/src/vs/min/ymaps.css';
function libPathResolver() {
    let libPath;
    if (ImSys.get().config.debug) {
        // 2026-08-19: путь из конфига сайта (debugModuleBasePath) имеет приоритет
        const customBase = ImSys.debugModuleBasePath?.replace(/\/+$/, '');
        if (customBase)
            libPath = customBase;
        else {
            const path = ImSys.siteBaseKey.split('.')[1];
            libPath = `/lib/imsys-ts/${path}`;
        }
    }
    else {
        // Динамически определяем путь на о
        const siteAttr = ImSys.siteBaseKey;
        let rootNode;
        if (siteAttr) {
            rootNode = siteAttr.split(".")[0]; // "vs"
            libPath = '/src/' + rootNode + '/min';
        }
        else
            libPath = '/src/vs/min';
    }
    return libPath;
}
/**
 * Инициализация и регистрация всех предустановленных виджетов.
 * Вызывается один раз при старте системы.
 */
function registerSystemWidgets() {
    // Провайдеры комманд
    UiCommandRegistry.uiCommands.registerProvider(new DefaultUiCommandProvider());
    UiCommandRegistry.uiCommands.registerProvider(new ItemsUiCommandProvider());
    ServerCmdProviderRegistry.serverCommands.registerProvider(new DefaultServerCmdProvider());
    // Регистрация view и table
    ViewWidget.register();
    TableWidget.register();
    // TODO: А он большой? Есть смысл в асинк? На будущее
    AirDatepickerWidget.register();
    // Регистрация основных виджетов для элементов
    ArchivalDateWidget.register();
    AsyncWidget.register();
    // 2026-08-14: AutocompleteWidget — замена ComboBoxWidget. Оба зарегистрированы
    // одновременно на время миграции: cssKey у них разные (im-autocomplete / im-combo),
    // поэтому на одном элементе они не пересекаются.
    AutocompleteWidget.register();
    CaptchaWidget.register();
    CollectionWidget.register();
    ComboBoxWidget.register();
    DropdownWidget.register();
    FilesizeWidget.register();
    InputCollectionWidget.register();
    ListboxWidget.register();
    NumboxWidget.register();
    RadioboxWidget.register();
    TabsPanelWidget.register();
    TabsWidget.register();
    TimecodeWidget.register();
    TripletWidget.register();
    MenuWidget.register();
    // Фильтр, сортировка
    SearchWidget.register();
    FilterWidget.register();
    SortWidget.register();
    PaginationWidget.register();
    // Отложенная регистрация
    WidgetRegistry.registerLazy("CmdPanel", async () => {
        const mod = await import('../widgets/CmdPanelWidget.js');
        return {
            ctor: mod.CmdPanelWidget
        };
    });
    WidgetRegistry.registerLazy("SchemaForm", async () => {
        const mod = await import('../widgets/schema-form/SchemaFormWidget.js');
        return {
            ctor: mod.SchemaFormWidget
        };
    }, "schema-form");
    WidgetRegistry.registerLazy("AIChat", async () => {
        const mod = await import('../widgets/chat/AIChatWidget.js');
        return { ctor: mod.AIChatWidget };
    }, "ai-chat");
    WidgetRegistry.registerLazy("Tree", async () => {
        const mod = await import('../widgets/tree/TreeWidget.js');
        return {
            ctor: mod.TreeWidget
        };
    }, 'im-tree');
    // UppyWidget
    WidgetRegistry.registerLazy("Uppy", // имя для [data-widget="Uppy"]
    async () => {
        // 1. грузим css
        // 2026-08-22: vendor-сборка Uppy лежит в статике ядра и одинакова
        // для debug и prod — цепочка fnc_postcss минифицирует оба режима,
        // а libPathResolver() в debug указывает на каталог js-бандла.
        await utils.loadCss(UPPY_VENDOR_CSS);
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/uppy/UppyWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.UppyWidget
        };
    });
    // PdfJsWidget
    WidgetRegistry.registerLazy("PdfJs", async () => {
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/PdfJsWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.PdfJsWidget
        };
    }, "im-pdfjs");
    // CkeditorWidget
    WidgetRegistry.registerLazy("Ckeditor", async () => {
        // TODO: Много вызовов здесь (вроде 4)
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/CkeditorWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.CkeditorWidget
        };
    }, 
    // TODO: Переименовать элемент (это не html)
    "im-html");
    WidgetRegistry.registerLazy("JsonEditor", async () => {
        const mod = await import('../widgets/JsonEditorWidget.js');
        return {
            ctor: mod.JsonEditorWidget
        };
    }, "im-json");
    // ViewCheckerWidget
    WidgetRegistry.registerLazy("ViewChecker", async () => {
        // TODO: Много вызовов здесь (вроде 4)
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/ViewCheckerWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.ViewCheckerWidget
        };
    }, "im-view-checker");
    // ItemsSelectorWidget
    WidgetRegistry.registerLazy("ItemsSelector", async () => {
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/itemsselector/ItemsSelectorWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.ItemsSelectorWidget
        };
    }, "im-itemsselector");
    // MessagesControllerWidget
    WidgetRegistry.registerLazy("MessagesController", async () => {
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/MessagesControllerWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.MessagesControllerWidget
        };
    }, "im-messages-controller");
    // 2026-09-22: TrackingChannelWatcherWidget - наблюдатель каналов Tracking (счётчики
    // каналов и колокольчик шапки). В ядре, потому что prod-режим сайта Tracking
    // подключает бандл ядра, а не бандл Nordbox.Site.
    WidgetRegistry.registerLazy("TrackingChannelWatcher", async () => {
        const mod = await import('../widgets/TrackingChannelWatcherWidget.js');
        return {
            ctor: mod.TrackingChannelWatcherWidget
        };
    }, "tracking-channel-watcher");
}
function registerCoreWidgets() {
    // ChartWidget
    WidgetRegistry.registerLazy("Chart", async () => {
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/ChartWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.ChartWidget
        };
    }, "im-chart");
    // CollectionTreeWidget
    WidgetRegistry.registerLazy("CollectionTree", async () => {
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/CollectionTreeWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.CollectionTreeWidget
        };
    }, "im-collection-tree");
    // DashboardLayoutWidget
    WidgetRegistry.registerLazy("DashboardLayout", async () => {
        await utils.loadCss(GRIDSTACK_VENDOR_CSS);
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/dashboard/DashboardLayoutWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.DashboardLayoutWidget
        };
    }, "im-widgetcollection");
    // ResizerWidget
    WidgetRegistry.registerLazy("Resizer", async () => {
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/ResizerWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.ResizerWidget
        };
    }, "im-resizer");
    // YMapsWidget
    WidgetRegistry.registerLazy("YMaps", async () => {
        await utils.loadCss(YMAPS_VENDOR_CSS);
        // 2. динамический импорт самого виджета
        const mod = await import('../widgets/ymaps/YMapsWidget.js');
        // 3. возвращаем конструктор 
        return {
            ctor: mod.YMapsWidget
        };
    });
}

export { GRIDSTACK_VENDOR_CSS, UPPY_VENDOR_CSS, YMAPS_VENDOR_CSS, libPathResolver, registerCoreWidgets, registerSystemWidgets };
